import type { ComponentType } from 'react';
import type { AdjustmentConfig } from '../../types/image-editor.types';
import {
  applyBrightnessContrastToCanvas,
  identityBrightnessContrastValue,
  type BrightnessContrastValue,
} from '../../lib/adjustments/brightness-contrast';
import {
  applyColorBalanceToCanvas,
  identityColorBalanceValue,
  type ColorBalanceValue,
} from '../../lib/adjustments/color-balance';
import {
  applyCurvesToCanvas,
  identityCurvesValue,
  type CurvesValue,
} from '../../lib/adjustments/curves';
import {
  applyHueSaturationToCanvas,
  identityHueSaturationValue,
  type HueSaturationValue,
} from '../../lib/adjustments/hue-saturation';
import {
  applyLevelsToCanvas,
  identityLevelsValue,
  type LevelsValue,
} from '../../lib/adjustments/levels';
import {
  applyShadowsHighlightsToCanvas,
  identityShadowsHighlightsValue,
  type ShadowsHighlightsValue,
} from '../../lib/adjustments/shadows-highlights';
import {
  applyVibranceToCanvas,
  identityVibranceValue,
  type VibranceValue,
} from '../../lib/adjustments/vibrance';
import { BrightnessContrastPanel } from './BrightnessContrastPanel';
import { ColorBalancePanel } from './ColorBalancePanel';
import { CurvesPanel } from './CurvesPanel';
import { HueSaturationPanel } from './HueSaturationPanel';
import { LevelsPanel } from './LevelsPanel';
import { ShadowsHighlightsPanel } from './ShadowsHighlightsPanel';
import { VibrancePanel } from './VibrancePanel';

/** Discriminator on `AdjustmentConfig` — every implemented dialog
 *  matches one of these. Picker entries can also reference future
 *  kinds (declared in the type union but no UI yet) which render as
 *  "Coming soon". */
export type AdjustmentKind = AdjustmentConfig['kind'];

/** Common props every adjustment Panel receives from
 *  `AdjustmentsModal`. Each panel is purely controlled — it owns no
 *  state of its own beyond ephemeral UI bits like the active channel
 *  chip. The modal owns the canonical `value` (the shape that ends
 *  up serialized into the layer config) and pipes preview + apply
 *  through the spec's `applyToCanvas`. */
export interface AdjustmentPanelProps<TValue> {
  value: TValue;
  onChange: (next: TValue) => void;
  /** 256-bucket histogram of the source layer. Computed once per
   *  open by the modal and passed to whichever panels want to render
   *  it as a backdrop (Curves, Levels). Other panels ignore it. */
  histogram?: number[];
}

/** Per-kind plumbing the modal needs. Adding a new dialog = add an
 *  entry to `ADJUSTMENT_SPECS` + flip `available` to `true` in the
 *  matching `ADJUSTMENT_MENU` row. */
export interface AdjustmentSpec<TValue> {
  kind: AdjustmentKind;
  /** Title in the modal header ("Adjust — Curves"). */
  title: string;
  /** "No-change" value for this kind — used as the starting state on
   *  open and as the value re-applied when the user cancels (so the
   *  live preview resets to identity before the dialog closes). */
  identity: () => TValue;
  /** Apply `value` to `target` from the cached `source`. Must be
   *  non-cumulative — calling it repeatedly with the same value must
   *  produce the same pixels. */
  applyToCanvas: (source: ImageData, target: HTMLCanvasElement, value: TValue) => void;
  /** The body of the modal for this kind (channel selector + sliders
   *  + curve editor / etc.). Modal supplies the chrome. */
  Panel: ComponentType<AdjustmentPanelProps<TValue>>;
}

/** Implemented kinds. `AdjustmentsModal` looks the active kind up in
 *  this map; if missing, the modal won't open (the picker renders
 *  the entry as disabled). */
// Spec values intentionally use unknown for the value generic at the
// map layer — each entry is internally well-typed, but the registry
// is heterogeneous by design. Casts to ComponentType<unknown> live at
// the dispatch site in AdjustmentsModal where the spec is fetched.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const ADJUSTMENT_SPECS: Partial<Record<AdjustmentKind, AdjustmentSpec<any>>> = {
  curves: {
    kind: 'curves',
    title: 'Curves',
    identity: identityCurvesValue,
    applyToCanvas: applyCurvesToCanvas,
    Panel: CurvesPanel,
  } satisfies AdjustmentSpec<CurvesValue>,
  levels: {
    kind: 'levels',
    title: 'Levels',
    identity: identityLevelsValue,
    applyToCanvas: applyLevelsToCanvas,
    Panel: LevelsPanel,
  } satisfies AdjustmentSpec<LevelsValue>,
  'brightness-contrast': {
    kind: 'brightness-contrast',
    title: 'Brightness/Contrast',
    identity: identityBrightnessContrastValue,
    applyToCanvas: applyBrightnessContrastToCanvas,
    Panel: BrightnessContrastPanel,
  } satisfies AdjustmentSpec<BrightnessContrastValue>,
  'hue-saturation': {
    kind: 'hue-saturation',
    title: 'Hue/Saturation',
    identity: identityHueSaturationValue,
    applyToCanvas: applyHueSaturationToCanvas,
    Panel: HueSaturationPanel,
  } satisfies AdjustmentSpec<HueSaturationValue>,
  'color-balance': {
    kind: 'color-balance',
    title: 'Color Balance',
    identity: identityColorBalanceValue,
    applyToCanvas: applyColorBalanceToCanvas,
    Panel: ColorBalancePanel,
  } satisfies AdjustmentSpec<ColorBalanceValue>,
  vibrance: {
    kind: 'vibrance',
    title: 'Vibrance',
    identity: identityVibranceValue,
    applyToCanvas: applyVibranceToCanvas,
    Panel: VibrancePanel,
  } satisfies AdjustmentSpec<VibranceValue>,
  'shadows-highlights': {
    kind: 'shadows-highlights',
    title: 'Shadows/Highlights',
    identity: identityShadowsHighlightsValue,
    applyToCanvas: applyShadowsHighlightsToCanvas,
    Panel: ShadowsHighlightsPanel,
  } satisfies AdjustmentSpec<ShadowsHighlightsValue>,
};

export type AdjustmentCategory = 'tonal' | 'color';

/** Picker menu — listed in the order the user sees them, grouped by
 *  category. Items whose `kind` isn't in `ADJUSTMENT_SPECS` render as
 *  disabled "Coming soon" rows. Adding a dialog =
 *    1. add the spec to `ADJUSTMENT_SPECS`
 *    2. flip the row's `available` flag here on. */
export interface AdjustmentMenuItem {
  kind: AdjustmentKind;
  title: string;
  category: AdjustmentCategory;
}

export const ADJUSTMENT_MENU: AdjustmentMenuItem[] = [
  { kind: 'curves', title: 'Curves', category: 'tonal' },
  { kind: 'levels', title: 'Levels', category: 'tonal' },
  { kind: 'brightness-contrast', title: 'Brightness/Contrast', category: 'tonal' },
  { kind: 'shadows-highlights', title: 'Shadows/Highlights', category: 'tonal' },
  { kind: 'hue-saturation', title: 'Hue/Saturation', category: 'color' },
  { kind: 'color-balance', title: 'Color Balance', category: 'color' },
  { kind: 'vibrance', title: 'Vibrance', category: 'color' },
];

export const CATEGORY_LABELS: Record<AdjustmentCategory, string> = {
  tonal: 'Tonal',
  color: 'Color',
};

/** User-facing display name for an adjustment kind — the same
 *  string as the menu entry's `title`, looked up by kind. Used
 *  for default layer names on the LayersPanel ("Curves 1",
 *  "Brightness/Contrast 2", etc.) and falls back to the kind
 *  literal for placeholder kinds that don't appear in the menu
 *  (selective-color, invert, etc.). */
export function adjustmentDisplayName(kind: AdjustmentKind): string {
  return ADJUSTMENT_MENU.find((m) => m.kind === kind)?.title ?? kind;
}
